import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { cloneDeep, get, set } from 'lodash';

import {
  FIELD_PATHS,
  FIELD_VALUES,
  URL_PARAM_APPLICANT_ID,
  URL_PARAM_APPLICATION_ID,
  URL_PARAM_ASSET_ID,
} from '@funnels/constants';
import { useCustomer, useDidUpdate } from '@funnels/hooks';
import { upsertApplicants, upsertAssets, upsertLiabilities, upsertRelatedParty } from '@funnels/redux-store';
import {
  AnyFormValues,
  Asset,
  FunnelConfig,
  Owner,
  ReduxStoreData,
  RelatedParty,
  StoreApplicant,
  UseParams,
} from '@funnels/types';
import { SubmittableForm } from '@funnels/ui-components';
import {
  filterOutTopUpPPSLiabilityAccountContracts,
  getIndexFromCustomerId,
  getIndexFromId,
  isExclusivelyOwnedByApplicantRole,
  setAccountantAndConveyancerInitialValues,
  setSpouseRelatedPartyInitialValues,
  setLicensedBuilderRelatedPartyInitialValues,
  setPropertyRelatedPartyInitialValues,
} from '@funnels/utils';

interface PostProductSelectProps {
  config: FunnelConfig;
}

function mapState(currentState: ReduxStoreData) {
  const data = {
    applications: currentState.applications.data,
    applicants: currentState.applicants.data,
    assets: currentState.assets.data,
    companies: currentState.companies.data,
    employments: currentState.employments.data,
    liabilities: currentState.liabilities.data,
    postProductSelect: currentState.postProductSelect.data,
    relatedParties: currentState.relatedParties.data,
  };
  const loading =
    currentState.applications.loading +
    currentState.applicants.loading +
    currentState.assets.loading +
    currentState.companies.loading +
    currentState.employments.loading +
    currentState.liabilities.loading +
    currentState.postProductSelect.loading +
    currentState.relatedParties.loading;

  const error =
    currentState.applications.error ||
    currentState.applicants.error ||
    currentState.assets.error ||
    currentState.companies.error ||
    currentState.employments.error ||
    currentState.liabilities.error ||
    currentState.postProductSelect.error ||
    currentState.relatedParties.error;

  return { data, loading, error };
}

const FORM_SUBMITTED_INITIAL_STATE = {
  submitted: false,
  hardRedirect: false,
  nextQuestionUrl: '',
  urlParams: {},
};

export function PostProductSelectForm(props: PostProductSelectProps) {
  const history = useHistory();
  const [formSubmitted, setFormSubmitted] = useState(FORM_SUBMITTED_INITIAL_STATE);
  const { data, loading, error } = useSelector(mapState);
  const errorMessage = get(error, 'message');
  const {
    [URL_PARAM_APPLICANT_ID]: applicantId,
    [URL_PARAM_APPLICATION_ID]: applicationId,
    [URL_PARAM_ASSET_ID]: assetId,
  } = useParams<UseParams>();
  const dispatch = useDispatch();
  const customer = useCustomer();
  const customerApplicantIndex = getIndexFromCustomerId(get(data, FIELD_PATHS.APPLICANTS(), []), customer.data.id);
  const customerRole = get(data, FIELD_PATHS.APPLICANT_TYPE(customerApplicantIndex), FIELD_VALUES.BORROWER);
  const applicants: StoreApplicant[] = get(data, FIELD_PATHS.APPLICANTS(), []).filter(
    (applicant: StoreApplicant) =>
      customerRole === get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TYPE(0), FIELD_VALUES.BORROWER) ||
      customerRole === FIELD_VALUES.BORROWER
  );

  // const relatedParties = get(data, FIELD_PATHS.RELATED_PARTIES(), []);

  const applicantIndex = getIndexFromId(applicants, applicantId);
  const assets = get(data, FIELD_PATHS.ASSETS(), []);
  const assetIndex = getIndexFromId(assets, assetId);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValues = useMemo(() => {
    // initialize relatedParties if it doesn't exists yet
    const cloneValues = cloneDeep(data);
    setPropertyRelatedPartyInitialValues(cloneValues, applicationId!);
    setAccountantAndConveyancerInitialValues(cloneValues, applicationId!);
    setSpouseRelatedPartyInitialValues(cloneValues);
    setLicensedBuilderRelatedPartyInitialValues(cloneValues, applicationId!);

    return cloneValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(nextQuestion: any, values: AnyFormValues, urlParams: UseParams = {}) {
    // PERSIST_DATA
    const applicants = get(values, FIELD_PATHS.APPLICANTS(), []);
    const assets: Asset[] = get(values, FIELD_PATHS.ASSETS(), []);
    const liabilities = get(values, FIELD_PATHS.LIABILITIES(), []);
    // Do not include related party that without a contact type for saving.
    // Normally, those without a type are initialised by front end
    const relatedParties = get(values, FIELD_PATHS.RELATED_PARTIES(), []).filter((relatedParty: RelatedParty) => {
      if (!get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0))) return false;
      const assetId = get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_ASSET_ID(0));
      if (!assetId) return true;
      const assetIndex = assets.findIndex((asset) => asset.id === assetId);
      const owners: Owner[] = get({ assets }, FIELD_PATHS.ASSET_OWNERS(assetIndex), []);
      // do not includes asset related party if current user is a borrower and the asset is exclusively owned by guarantor
      return (
        customerRole !== FIELD_VALUES.BORROWER ||
        !isExclusivelyOwnedByApplicantRole(owners, applicants, FIELD_VALUES.GUARANTOR)
      );
    });

    dispatch(upsertApplicants(applicationId!, applicants));
    dispatch(upsertAssets(applicationId!, assets));
    dispatch(upsertRelatedParty(applicationId!, relatedParties));
    dispatch(upsertLiabilities(applicationId!, liabilities));

    setFormSubmitted({
      submitted: true,
      hardRedirect: nextQuestion.hardRedirect,
      nextQuestionUrl: nextQuestion.url,
      urlParams,
    });
  }

  const goToNextQuestion = (url: string, urlParams: UseParams) => {
    const newURL = generatePath(url, urlParams);
    history.push(newURL);
  };

  const redirect = (url: string, urlParams: UseParams) => {
    const newURL = generatePath(url, urlParams);
    window.location.assign(newURL);
  };

  // to handle redirection after form submit and requests
  useDidUpdate(() => {
    const { submitted, hardRedirect, nextQuestionUrl, urlParams } = formSubmitted;
    if (submitted && !hardRedirect && !loading && nextQuestionUrl) {
      goToNextQuestion(nextQuestionUrl, urlParams);
      setFormSubmitted(FORM_SUBMITTED_INITIAL_STATE);
    } else if (submitted && hardRedirect === true && !loading && nextQuestionUrl) {
      if (!errorMessage) {
        redirect(nextQuestionUrl, urlParams);
        setFormSubmitted(FORM_SUBMITTED_INITIAL_STATE);
      }
    }
  }, [formSubmitted, loading]);

  return (
    <SubmittableForm
      {...props}
      {...{ assetIndex, applicantIndex }}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      funnelType="funnelTwo"
    />
  );
}
